// File: src/pages/Dashboard.jsx

import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Spinner, Icon } from '@shopify/polaris';
import {
  LockIcon,
  AlertTriangleIcon,
  MoneyIcon,
  ChartLineIcon,
  TeamIcon,
  OrderIcon,
  AlertCircleIcon,
  WalletIcon,
  ArrowRightIcon,
} from '@shopify/polaris-icons';

import LayoutWrapper from '../components/Layout/Layout';
import { AuthContext } from '../context/AuthContext';
import './Dashboard.css';
import ReactMarkdown from 'react-markdown';
import useData from '../hooks/useData';
import EmbeddedFullPageBanner from '../components/EmbeddedFullPageBanner';
import DateRangePills from '../components/ui/DateRangePills';
import WelcomeHub from '../components/onboarding/WelcomeHub';
import NavCoachmark from '../components/onboarding/NavCoachmark';
import { useFirstTimeUser } from '../hooks/useFirstTimeUser';


const SkeletonCard = () => (
  <div className="dashboard-card skeleton">
    <div className="skeleton-header">
      <div className="skeleton-icon"></div>
      <div className="skeleton-title"></div>
    </div>
    <div className="skeleton-metrics">
      <div className="skeleton-metric"></div>
      <div className="skeleton-metric"></div>
      <div className="skeleton-metric"></div>
    </div>
    <div className="skeleton-insights">
      <div className="skeleton-insight-heading"></div>
      <div className="skeleton-insight-item"></div>
      <div className="skeleton-insight-item"></div>
    </div>
    <div className="skeleton-footer"></div>
  </div>
);

// New StoryCard component to replace MainCard
const StoryCard = React.memo(({ card, prescription, onCardClick, handleOpenMainCardModal }) => {
  

  const getIconClass = (urgency) => {
    switch (urgency) {
      case 'critical': return 'story-card-icon-critical';
      case 'high': return 'story-card-icon-warning';
      case 'excellent': return 'story-card-icon-excellent';
      case 'warning': return 'story-card-icon-warning';
      case 'good': return 'story-card-icon-good';
      default: return 'story-card-icon-neutral';
    }
  };

  // Use prescription urgency if available, otherwise card's urgency
  const urgency = prescription?.urgency || card.urgency || 'neutral';
  
  // Use prescription title/description if available, otherwise card's headline
  const headline = prescription?.title || card.headline;
  const description = prescription?.description || null;

  return (
    <div
     className={`story-card story-card-${card.key}`}

      onClick={() => onCardClick?.(card.path)}
      role="button"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onCardClick?.(card.path)}
    >
      <div className="story-card-header">
        <div className={`story-card-icon ${getIconClass(urgency)}`}>
          <Icon source={card.icon} />
        </div>
        <div className="story-card-titles">
          <h3 className="story-card-title">{card.title}</h3>
          <p className="story-card-subtitle">{card.subtitle}</p>
        </div>
        <span
          onClick={(e) => {
            e.stopPropagation();
            handleOpenMainCardModal(card);
          }}
          className="info-icon"
        >
          <Icon source={AlertCircleIcon} />
        </span>
      </div>

      <div className="story-card-headline">{headline}</div>
      {description && (
        <p className="story-card-description">{description}</p>
      )}

      <div className="story-card-footer">
        <Link to={card.path} className={`cta-button story-card-cta cta-${urgency}`}>
          {card.cta}
          <Icon source={ArrowRightIcon} />
        </Link>
      </div>
    </div>
  );
});

export default function Dashboard() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

  const [siteId, setSiteId] = useState(null); // Don't initialize from sessionStorage yet
  const [platform, setPlatform] = useState(null); // Don't initialize from sessionStorage yet
  const [sitesProcessed, setSitesProcessed] = useState(false);
  const [error] = useState(null);

  const [daysRange, setDaysRange] = useState('7');

  const handleRangeChange = useCallback((value) => {
    setDaysRange(value);
    sessionStorage.setItem('days_range', value);
  }, []);

  const [savedPropertyId, setSavedPropertyId] = useState(null);
  const [, setSavedPropertyLoading] = useState(true);
  const [savedPropertyError, setSavedPropertyError] = useState(null);

  const [modalActive, setModalActive] = useState(false);
  const [activeKpi, setActiveKpi] = useState(null);

  const handleOpenModal = useCallback((kpi) => {
    setActiveKpi(kpi);
    setModalActive(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setActiveKpi(null);
    setModalActive(false);
  }, []);

  const [mainCardModalActive, setMainCardModalActive] = useState(false);
  const [activeMainCard, setActiveMainCard] = useState(null);

  const handleOpenMainCardModal = useCallback((card) => {
    setActiveMainCard(card);
    setMainCardModalActive(true);
  }, []);

  const handleCloseMainCardModal = useCallback(() => {
    setActiveMainCard(null);
    setMainCardModalActive(false);
  }, []);

  const handleCardClick = useCallback(
    (path) => {
      if (!path) return;

      // Open the sidebar whenever we leave the dashboard
      // (Sidebar will listen too, but this helps if you persist collapsed state)
      localStorage.setItem('sidebar_collapsed', 'false');

      navigate(path);
    },
    [navigate],
  );

  // Clear session storage on token change
  useEffect(() => {
    if (!token) return;
    const last = sessionStorage.getItem('site_owner_token');
    if (last && last !== token) {
      // Token changed, clear site data
      sessionStorage.removeItem('site_id');
      sessionStorage.removeItem('site_token');
      sessionStorage.removeItem('site_platform');
      sessionStorage.removeItem('days_range');
      setSiteId(null);
      setPlatform(null);
      setSitesProcessed(false);
    }
    sessionStorage.setItem('site_owner_token', token);
  }, [token]);

  // Fetch sites on component mount or token change
  const [sitesCacheBuster, setSitesCacheBuster] = useState(0);
  const { data: sites = [], loading: sitesLoading, refetch: refetchSites } = useData(
    token ? `sites:${token}:${sitesCacheBuster}` : 'noop',
    () => fetch(`${API}/api/sites`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token, sitesCacheBuster] },
  );

  // Check for a flag indicating we just added a new site
  useEffect(() => {
    const newSiteAdded = sessionStorage.getItem('new_site_added');
    if (newSiteAdded === 'true') {
      sessionStorage.removeItem('new_site_added');
      setSitesCacheBuster(prev => prev + 1);
      if (refetchSites) {
        refetchSites();
      }
    }
  }, [refetchSites]);

  useEffect(() => {
    // Only process sites after loading is complete
    if (sitesLoading) return;

    // Check if we already have a valid site_id in sessionStorage
    const storedSiteId = sessionStorage.getItem('site_id');
    const storedSiteToken = sessionStorage.getItem('site_token');

    // If we have valid stored values and they're not 'null' strings, use them
    if (storedSiteId && storedSiteToken && storedSiteId !== 'null' && storedSiteToken !== 'null') {
      setSiteId(storedSiteId);
      const site = sites.find((s) => String(s.id) === storedSiteId);
      if (site) {
        setPlatform(site.platform || null);
        sessionStorage.setItem('site_platform', site.platform || '');
      }
      setSitesProcessed(true);
      return;
    }

    // Otherwise, choose the first site from the fetched sites
    if (Array.isArray(sites) && sites.length > 0) {
      const firstSite = sites[0];
      const firstSiteId = String(firstSite.id);
      const firstSiteToken = String(firstSite.token);
      const firstSitePlatform = firstSite.platform;
      sessionStorage.setItem('site_id', firstSiteId);
      sessionStorage.setItem('site_token', firstSiteToken);
      sessionStorage.setItem('site_platform', firstSitePlatform || '');

      setSiteId(firstSiteId);
      setPlatform(firstSitePlatform || null);
      setSitesProcessed(true);
    } else if (!sitesLoading) {
      // Only mark as processed and potentially redirect if we're done loading
      setSitesProcessed(true);
    }
  }, [sites, sitesLoading]);

  // Fetch GA4 property after sites are loaded
  const {
    data: ga4Prop,
    loading: ga4PropLoading,
    error: ga4PropError,
  } = useData(
    token ? `ga4PropertyId:${token}` : 'noop',
    () => fetch(`${API}/integrations/property`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] },
  );

  useEffect(() => {
    if (!ga4PropLoading && !ga4PropError) {
      setSavedPropertyId(ga4Prop?.propertyId || ga4Prop?.id || null);
    }
    setSavedPropertyLoading(ga4PropLoading);
    setSavedPropertyError(ga4PropError);
  }, [ga4Prop, ga4PropLoading, ga4PropError]);

  // Redirect if no sites after loading - with better handling for new site additions
  // Redirect if no sites after loading - with better handling for new site additions
  useEffect(() => {
    // Wait for AppWarmup to complete
    if (sessionStorage.getItem('app_warmup_complete') !== 'true') return;

    // Add 2 second delay before checking
    const delayTimeout = setTimeout(() => {
      if (!sitesLoading && sitesProcessed && Array.isArray(sites) && sites.length === 0) {
        const storedSiteId = sessionStorage.getItem('site_id');
        const storedSiteToken = sessionStorage.getItem('site_token');

        if (!storedSiteId || !storedSiteToken || storedSiteId === 'null' || storedSiteToken === 'null') {
          let pollCount = 0;
          const maxPolls = 10; // Changed from 3

          const pollInterval = setInterval(() => {
            pollCount++;

            if (refetchSites) {
              refetchSites();
            }

            if (pollCount >= maxPolls) {
              clearInterval(pollInterval);
              navigate('/how-it-works', { replace: true });
            }
          }, 3000); // Changed from 2000

          return () => clearInterval(pollInterval);
        }
      }
    }, 2000);

    return () => clearTimeout(delayTimeout);
  }, [sitesLoading, sitesProcessed, sites, navigate, refetchSites]);
  // Only fetch data when we have a valid siteId
  const shouldFetchData = !!(sitesProcessed && siteId && siteId !== 'null' && token);

  // Use the useData hook to fetch all necessary data
  const { data: summary, loading: summaryLoading, error: summaryError } = useData(
    shouldFetchData ? `customer-intel:metrics:${siteId}:${daysRange}` : 'noop',
    shouldFetchData
      ? () =>
        fetch(`${API}/api/dashboard/summary/detailed?site_id=${siteId}&days=${daysRange}&force=1&_=${Date.now()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-store',
            'Pragma': 'no-cache',
          },
          cache: 'no-store',
        }).then((r) => r.json())
      : null,
    //{ ttlMs: 0, deps: [siteId, daysRange, token, shouldFetchData] },
    { ttlMs: 5 * 60 * 1000, deps: [siteId, daysRange, token, shouldFetchData] },
    
  );

  const { data: sessionsData, loading: sessionsLoading, error: sessionsError } = useData(
    shouldFetchData ? `businessSessions:${siteId}:${daysRange}` : 'noop',
    shouldFetchData
      ? () =>
        fetch(`${API}/api/business/sessions?site_id=${siteId}&days=${daysRange}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => r.json())
      : null,
    //{ ttlMs: 0, deps: [siteId, daysRange, token, shouldFetchData] },
    { ttlMs: 5 * 60 * 1000, deps: [siteId, daysRange, token, shouldFetchData] },
  );

  // 7-day baseline for Visitors benchmark (recent normal)
  const { data: sessionsBaseline, loading: sessionsBaselineLoading } = useData(
    shouldFetchData ? `businessSessionsBaseline:${siteId}:7` : 'noop',
    shouldFetchData
      ? () =>
        fetch(`${API}/api/business/sessions?site_id=${siteId}&days=7`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => r.json())
      : null,
    //{ ttlMs: 0, deps: [siteId, token, shouldFetchData] },
    { ttlMs: 60 * 60 * 1000, deps: [siteId, token, shouldFetchData] },
  );

  const { data: overview, loading: overviewLoading, error: overviewError } = useData(
    shouldFetchData ? `overview:${siteId}:${daysRange}` : 'noop',
    shouldFetchData
      ? () =>
        fetch(`${API}/api/business/overview?site_id=${siteId}&days=${daysRange}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => r.json())
      : null,
   { ttlMs: 5 * 60 * 1000, deps: [siteId, daysRange, token, shouldFetchData] },
   //{ ttlMs: 0, deps: [siteId, daysRange, token, shouldFetchData] },
  );

  const {
    data: topProductsData,
    loading: topProductsLoading,
    error: topProductsError,
  } = useData(
    shouldFetchData ? `customer-intel:top-products:${siteId}:${daysRange}` : 'noop',
    shouldFetchData
      ? () =>
        fetch(`${API}/api/customer-intelligence/top-products?site_id=${siteId}&days=${daysRange}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => r.json())
      : null,
    //{ ttlMs: 0, deps: [siteId, daysRange, token, shouldFetchData] },
    { ttlMs: 60 * 60 * 1000, deps: [siteId, daysRange, token, shouldFetchData] },
  );

  const { data: ga4Metrics, loading: ga4Loading, error: ga4Error } = useData(
    shouldFetchData && savedPropertyId ? `ga4Perf:${savedPropertyId}:${daysRange}` : 'noop',
    shouldFetchData && savedPropertyId
      ? () =>
        fetch(
          `${API}/api/business/performance?days=${daysRange}&propertyId=${encodeURIComponent(
            savedPropertyId,
          )}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ).then((r) => r.json())
      : null,
    //{ ttlMs: 0, deps: [savedPropertyId, daysRange, token, shouldFetchData] },
    { ttlMs: 60 * 60 * 1000, deps: [savedPropertyId, daysRange, token, shouldFetchData] },
  );

  // eslint-disable-next-line no-unused-vars
  const { data: pageViews = [], loading: pageViewsLoading, error: pageViewsError } = useData(
    shouldFetchData && savedPropertyId ? `pageViews:${savedPropertyId}:5` : 'noop',
    shouldFetchData && savedPropertyId
      ? () =>
        fetch(
          `${API}/api/business/page-views?limit=5&propertyId=${encodeURIComponent(savedPropertyId)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ).then((r) => r.json())
      : null,
    { ttlMs: 60 * 60 * 1000, deps: [savedPropertyId, token, shouldFetchData] },
    //{ ttlMs: 0, deps: [savedPropertyId, token, shouldFetchData] },
  );

// ============================================
// PRESCRIPTIONS FETCH
// ============================================
const { data: prescriptions = [] } = useData(
  siteId ? `prescriptions:${siteId}` : 'noop',
  () => fetch(`${API}/api/prescriptions?site_id=${siteId}&status=active`, {
    headers: { Authorization: `Bearer ${token}` }
  }).then(r => r.ok ? r.json() : []),
  { ttlMs: 60 * 60 * 1000, deps: [siteId, token] }
);

// Helper to get highest urgency prescription for a category
const getPrescriptionForCategory = useCallback((category) => {
  // SAFETY CHECK: Ensure prescriptions is actually an array
  if (!Array.isArray(prescriptions) || prescriptions.length === 0) return null;
  
  const categoryPrescriptions = prescriptions
    .filter(p => p.category === category)
    .sort((a, b) => {
      const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      return (urgencyOrder[a.urgency] || 3) - (urgencyOrder[b.urgency] || 3);
    });
  
  return categoryPrescriptions[0] || null;
}, [prescriptions]);


// Get prescriptions for each category
const salesPerfPrescription = useMemo(() => getPrescriptionForCategory('product_performance'), [getPrescriptionForCategory]);
const lostRevenuePrescription = useMemo(() => getPrescriptionForCategory('conversion'), [getPrescriptionForCategory]);
const growthPrescription = useMemo(() => getPrescriptionForCategory('growth'), [getPrescriptionForCategory]);
  // Normalize detailed metrics into the names your UI already uses
  const orders7d =
    summary?.orders_7d ??
    summary?.metrics?.orders_7d ??
    summary?.orders?.last7d?.count ??
    summary?.ordersLast7d ??
    summary?.orders_last_7d ??
    0;

  const revenue7d =
    summary?.sales_7d ??
    summary?.metrics?.sales_7d ??
    summary?.sales?.last7d?.amount ??
    summary?.revenue_7d ??
    summary?.revenueLast7d ??
    summary?.sales_last_7d ??
    0;

  const newAccounts7d =
    summary?.new_accounts_7d ??
    summary?.customers?.last7d?.count ??
    0;

  const orderCount = Number.isFinite(orders7d) ? orders7d : 0;
  //const dailyRevenue = 0; // keep legacy field but don’t use it
  const periodRevenue = Number(revenue7d) || 0;

  // Keep existing names your components reference
  const newClients = Number(newAccounts7d) || 0;
  const avgNewClients = Number(summary?.avgNewClients ?? 0);
  const avgOrderCount = Number(summary?.avgOrderCount ?? 0);
  const avgDailyRevenue = Number(summary?.avgDailyRevenue ?? 0);
  const revenueGrowth = Number(summary?.revenueGrowth ?? 0);
  const recentSessions = useMemo(() => {
    const rs = summary?.recentSessions;
    return Array.isArray(rs) ? rs : [];
  }, [summary?.recentSessions]);

  const keyFacts = summary?.keyFacts || {};

  // --- Active/Recent session helpers ---
  const ACTIVE_THRESHOLD_MINUTES = 5;
  const [nowTs, setNowTs] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 30000); // update every 30s
    return () => clearInterval(id);
  }, []);

  const pickStartTs = (s) => {
    // prefer freshest activity
    const iso =
      s?.last_event_at ||
      s?.lastEventAt ||
      s?.last_activity ||
      s?.lastActivity ||
      s?.start ||
      s?.timestamp;
    return iso ? new Date(iso).getTime() : null;
  };

  const isEnded = (s) =>
    Boolean(s?.ended || s?.end || s?.ended_at || s?.exit_at || s?.session_end || s?.completed);

  const pickPath = (s) =>
    s?.entryPage || s?.landingPage || s?.page_path || s?.exitPage || '/homepage';

  const activeSessions = useMemo(() => {
    return recentSessions.filter((s) => {
      if (isEnded(s)) return false; // don’t show ended sessions as “Live”
      const ts = pickStartTs(s);
      return ts && (nowTs - ts) / 60000 <= ACTIVE_THRESHOLD_MINUTES;
    });
  }, [recentSessions, nowTs]);

  const averageOrderValue = Number(overview?.average_order_value ?? 0);
  const highestExit = overview?.highest_exit || overview?.highestExit || null;
  const topProductsNames = (topProductsData?.top_products || [])
    .map((p) => p.product_name || p.name)
    .filter(Boolean);
  // eslint-disable-next-line no-unused-vars
  const totalPageViews = Array.isArray(pageViews)
    ? pageViews.reduce((s, pv) => s + (Number(pv.views) || 0), 0)
    : 0;

  const fmtMoney = useCallback((v) => (Number.isFinite(v) ? `$${v.toFixed(2)}` : '—'), []);
  const fmtInt = (v) => (Number.isFinite(v) ? Math.round(v).toLocaleString() : '—');
  const fmtDecimal = (v, decimals = 2) => (Number.isFinite(v) ? v.toFixed(decimals) : '—');

  // Safely coerce to Number or return null
  const toNum = useCallback((v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }, []);

  const baselineDays = 7;
  const baselineDailyVisitors = sessionsBaseline?.sessions
    ? Number(sessionsBaseline.sessions) / baselineDays
    : null;
  const expectedVisitorsForRange =
    baselineDailyVisitors != null ? baselineDailyVisitors * Math.max(1, parseInt(daysRange, 10)) : null;

  // Expected counts for other KPIs based on their 7-day per-day averages
  const expectedOrdersForRange = Number.isFinite(avgOrderCount)
    ? Math.round(avgOrderCount * Math.max(1, parseInt(daysRange, 10)))
    : null;
  const expectedRevenueForRange = Number.isFinite(avgDailyRevenue)
    ? avgDailyRevenue * Math.max(1, parseInt(daysRange, 10))
    : null;

  // Enhanced getDynamicStoryHeadline function with better data utilization
  const getDynamicStoryHeadline = useCallback((type, data) => {
    const {
      sessions,
      conversionRate,
      aov,
      revenueGrowth,
      engagementRate,
      exitRate,
      topProducts,
      orderCount,
      periodRevenue,
      daysRange,
      newClients,
      avgNewClients,
      avgOrderCount,
      avgDailyRevenue,
      bounceRate,
    } = data;

    // Helper function to determine urgency level
    switch (type) {
      case 'revenue': {
        const growth = toNum(revenueGrowth);
        const conv = toNum(conversionRate);
        const avgAOV = toNum(aov);
        const rev = toNum(periodRevenue);
        const expectedRevenue = toNum(avgDailyRevenue) * parseInt(daysRange, 10);

        // Priority 1: Significant revenue change
        if (growth != null && Math.abs(growth) > 15) {
          if (growth > 0) {
            return {
              headline: `Revenue up ${Math.round(growth)}% - ${fmtMoney(rev)} earned!`,
              cta: "Analyze Now",
              urgency: 'excellent'
            };
          } else {
            return {
              headline: `Revenue dropped ${Math.abs(Math.round(growth))}% to ${fmtMoney(rev)}`,
              cta: "Analyze Now",
              urgency: 'critical'
            };
          }
        }

        // Priority 2: Revenue vs expected
        if (rev && expectedRevenue && Math.abs(rev - expectedRevenue) > expectedRevenue * 0.1) {
          const diff = rev - expectedRevenue;
          const percentage = Math.round((diff / expectedRevenue) * 100);
          if (diff > 0) {
            return {
              headline: `${fmtMoney(Math.abs(diff))} above target (${percentage}% over)`,
              cta: "Analyze Now",
              urgency: 'excellent'
            };
          } else {
            return {
              headline: `${fmtMoney(Math.abs(diff))} below target (${Math.abs(percentage)}% under)`,
              cta: "Analyze Now",
              urgency: 'warning'
            };
          }
        }

        // Priority 3: Low conversion rate with decent traffic
        if (conv != null && conv < 2 && toNum(sessions) > 100) {
          return {
            headline: `${conv.toFixed(1)}% conversion - ${Math.round((2 - conv) / 100 * sessions)} lost sales`,
            cta: "Analyze Now",
            urgency: 'warning'
          };
        }

        // Priority 4: Low AOV with good order volume
        if (avgAOV != null && avgAOV < 75 && toNum(orderCount) > 10) {
          const potential = (100 - avgAOV) * toNum(orderCount);
          return {
            headline: `${fmtMoney(avgAOV)} avg order - ${fmtMoney(potential)} revenue potential`,
            cta: "Analyze Now",
            urgency: 'warning'
          };
        }

        // Default: Current performance
        return {
          headline: `${fmtMoney(rev)} earned in ${daysRange} days`,
          cta: "Analyze Now",
          urgency: 'neutral'
        };
      }

      case 'losses': {
        const sessionsNum = toNum(sessions);
        const convRate = toNum(conversionRate) || 2; // Assume 2% baseline
        const aovNum = toNum(aov) || 50; // Assume $50 baseline
        const exitRateNum = toNum(exitRate);
        const engagementRateNum = toNum(engagementRate);
        const bounceRateNum = toNum(bounceRate);

        // Priority 1: High bounce rate
        if (bounceRateNum != null && bounceRateNum > 70 && sessionsNum > 50) {
          const bouncedSessions = Math.round(sessionsNum * (bounceRateNum / 100));
          const lostRevenue = bouncedSessions * (convRate / 100) * aovNum;
          return {
            headline: `${bounceRateNum}% bounce rate - ${fmtMoney(lostRevenue)} in lost sales`,
            cta: "Analyze Now",
            urgency: 'critical'
          };
        }

        // Priority 2: Cart abandonment (calculated)
        /*if (sessionsNum > 20) {
          const addToCartRate = 0.7; // Industry average
          const potentialBuyers = sessionsNum * addToCartRate;
          const actualBuyers = sessionsNum * (convRate / 100);
          const abandonedCarts = potentialBuyers - actualBuyers;
          const abandonedRevenue = abandonedCarts * aovNum;
    
          if (abandonedRevenue > 500) {
            return {
              headline: `${Math.round(abandonedCarts)} abandoned carts = ${fmtMoney(abandonedRevenue)} lost`,
              cta: "Analyze Now",
              urgency: 'critical'
            };
          }
        }*/

        // Priority 3: High exit rate on key pages
        if (exitRateNum != null && exitRateNum > 60) {
          const exitingSessions = Math.round(sessionsNum * (exitRateNum / 100));
          const lostRevenue = exitingSessions * (convRate / 100) * aovNum;
          return {
            headline: `${exitRateNum}% exit rate bleeding ${fmtMoney(lostRevenue)}`,
            cta: "Analyze Now",
            urgency: 'warning'
          };
        }

        // Priority 4: Low engagement
        if (engagementRateNum != null && engagementRateNum < 50 && sessionsNum > 30) {
          const disengagedSessions = Math.round(sessionsNum * ((100 - engagementRateNum) / 100));
          return {
            headline: `${100 - engagementRateNum}% not engaging - ${disengagedSessions} missed opportunities`,
            cta: "Analyze Now",
            urgency: 'warning'
          };
        }

        // Default: General conversion opportunity
        const missedConversions = sessionsNum - (sessionsNum * (convRate / 100));
        const lostRevenue = missedConversions * (convRate / 100) * aovNum;
        return {
          headline: sessionsNum > 0
            ? `${Math.round(100 - convRate)}% of visitors leave - ${fmtMoney(lostRevenue)} opportunity`
            : `No visitor data yet - install tracking script`,
          cta: "Analyze Now",
          urgency: 'warning'
        };
      }

      case 'growth': {
        const orderCountNum = toNum(orderCount);
        const sessionsNum = toNum(sessions);
        const newClientsNum = toNum(newClients);
        const expectedOrders = toNum(avgOrderCount) * parseInt(daysRange, 10);
        const expectedClients = toNum(avgNewClients) * parseInt(daysRange, 10);

        // Priority 1: Hot product opportunity
        if (topProducts.length > 0) {
          const topProduct = topProducts[0];
          if (orderCountNum > expectedOrders * 1.2) {
            return {
              headline: `"${topProduct}" is hot - ${orderCountNum} orders placed`,
              cta: "Analyze Now",
              urgency: 'excellent'
            };
          } else {
            return {
              headline: `"${topProduct}" trending - push for more sales`,
              cta: "Analyze Now",
              urgency: 'good'
            };
          }
        }

        // Priority 2: Order momentum vs baseline
        if (orderCountNum && expectedOrders) {
          if (orderCountNum > expectedOrders * 1.3) {
            return {
              headline: `${orderCountNum} orders - ${Math.round(((orderCountNum - expectedOrders) / expectedOrders) * 100)}% above normal!`,
              cta: "Analyze Now",
              urgency: 'excellent'
            };
          } else if (orderCountNum < expectedOrders * 0.7) {
            const missedOrders = Math.round(expectedOrders - orderCountNum);
            return {
              headline: `${missedOrders} fewer orders than expected - growth stalled`,
              cta: "Analyze Now",
              urgency: 'warning'
            };
          }
        }

        // Priority 3: New customer acquisition vs baseline
        if (newClientsNum && expectedClients) {
          if (newClientsNum > expectedClients * 1.2) {
            return {
              headline: `${newClientsNum} new customers - acquisition accelerating`,
              cta: "Analyze Now",
              urgency: 'excellent'
            };
          } else if (newClientsNum < expectedClients * 0.8) {
            const missedClients = Math.round(expectedClients - newClientsNum);
            return {
              headline: `${missedClients} fewer new customers than normal`,
              cta: "Analyze Now",
              urgency: 'warning'
            };
          }
        }

        // Priority 4: Traffic without conversions
        if (sessionsNum > 1000 && orderCountNum < 20) {
          const conversionRate = (orderCountNum / sessionsNum) * 100;
          return {
            headline: `${Math.round(sessionsNum / 1000)}k visitors, ${conversionRate.toFixed(1)}% converting`,
            cta: "Analyze Now",
            urgency: 'warning'
          };
        }

        // Priority 5: Repeat customer opportunity
        if (orderCountNum > newClientsNum * 2) {
          const repeatRate = Math.round(((orderCountNum - newClientsNum) / orderCountNum) * 100);
          return {
            headline: `${repeatRate}% repeat customers driving growth`,
            cta: "Analyze Now",
            urgency: 'good'
          };
        }

        // Default: General growth opportunity
        return {
          headline: `Growth opportunities detected in your data`,
          cta: "Analyze Now",
          urgency: 'neutral'
        };
      }

      default:
        return {
          headline: 'Loading insights...',
          cta: 'Analyze Now',
          urgency: 'neutral'
        };
    }
  }, [toNum, fmtMoney]);

  const mainCardDetails = [
    {
      title: 'Customer Intelligence',
      subtitle: "Who's buying and what keeps them coming back",
      path: '/business',
      icon: MoneyIcon,
      type: 'revenue',
      ...getDynamicStoryHeadline('revenue', {
        periodRevenue,
        revenueGrowth,
        conversionRate: keyFacts.conversionRate,
        aov: averageOrderValue,
        daysRange,
        sessions: sessionsData?.sessions,
        orderCount,
        avgDailyRevenue,
      }),
      popup: {
        title: 'Customer Intelligence',
        body: `**Definition:** Insights into who your customers are, how they behave, and what makes them loyal.

**How to think about it:** Which groups repeat? Who’s at risk of churning? What patterns—looping, backtracking, product affinity—signal opportunity?

**Why it matters to your business:** The clearer your understanding of customer behavior, the better you can drive retention, repeat purchases, and long-term revenue.`,
      },
    },
    {
      title: 'Experience & Conversion',
      subtitle: "Where friction happens—and how to fix it",
      path: '/performance',
      icon: AlertTriangleIcon,
      type: 'losses',
      ...getDynamicStoryHeadline('losses', {
        sessions: sessionsData?.sessions,
        conversionRate: keyFacts.conversionRate,
        aov: averageOrderValue,
        engagementRate: ga4Metrics?.engagement_rate_pct,
        exitRate: highestExit?.exit_rate_pct,
        bounceRate: ga4Metrics?.bounce_rate_pct,
        daysRange,
      }),
      popup: {
        title: 'Experience & Conversion',
        body: `**Definition:**  Measures where customers get stuck, confused, or frustrated during the shopping process.

**How to think about it:** Which pages leak the most conversions? Where do rage clicks, exits, or high friction appear?

**Why it matters:** Fixing friction unlocks “found revenue.” Every improvement here boosts conversion without spending more on traffic.`,
      },
    },
    {
      title: 'Growth & Strategy',
      subtitle: "What’s working—and what to do next",
      path: '/facts',
      icon: ChartLineIcon,
      type: 'growth',
      ...getDynamicStoryHeadline('growth', {
        topProducts: topProductsNames,
        orderCount,
        sessions: sessionsData?.sessions,
        newClients,
        avgNewClients,
        avgOrderCount,
        daysRange,
      }),
      popup: {
        title: 'Growth & Strategy',
        body: `**Definition:** Signals that reveal momentum, emerging opportunities, and the highest-ROI next steps.
    
  **How to think about it:** Which channels are performing? Which products should be promoted? What strategic moves drive the biggest lift?
    
  **Why it matters:** Clear insights help you invest energy where it multiplies—faster growth, smarter marketing, stronger positioning.`,
      },
    },
  ];

const cardPrescriptions = {
  'Sales Performance': salesPerfPrescription,
  'Lost Revenue': lostRevenuePrescription,
  'Growth Opportunities': growthPrescription,
};

  // ============================================
  //  WELCOME HUB / FIRST-TIME USER DETECTION
  //  These hooks must run on every render — placed BEFORE any early return
  //  so the order of hook calls is identical across renders (rules-of-hooks).
  // ============================================
  const activeSite = useMemo(() => {
    if (!Array.isArray(sites) || sites.length === 0) return null;
    return sites.find((s) => Number(s.id) === Number(siteId)) || sites[0];
  }, [sites, siteId]);

  const storeName =
    activeSite?.name ||
    activeSite?.shop_domain ||
    activeSite?.shopDomain ||
    activeSite?.url ||
    activeSite?.domain ||
    'your store';

  const storeUrl =
    activeSite?.shop_domain ||
    activeSite?.shopDomain ||
    activeSite?.url ||
    activeSite?.domain ||
    null;

  // Heuristic: if all key data points are empty after loading, treat as "no data."
  // While loading, signals are an empty object so hasNoData stays false (no flicker).
  const dataLoading = sessionsLoading || overviewLoading || pageViewsLoading;
  const welcomeSignals = dataLoading
    ? {}
    : {
        sessions: sessionsData?.sessions ?? 0,
        orders: overview?.orders ?? 0,
        pageViews: Array.isArray(pageViews) ? pageViews.length : 0,
        prescriptions: Array.isArray(prescriptions) ? prescriptions.length : 0,
      };

  const { shouldShowWelcome, dismiss: dismissWelcome } = useFirstTimeUser(
    welcomeSignals,
    { siteId },
  );

  const [showNavCoachmark, setShowNavCoachmark] = useState(false);

  // Live visitor counter for WelcomeHub.
  // The cached sessions data has a 5-minute TTL — too slow for the "visit your store and watch
  // yourself appear" moment. While WelcomeHub is open, poll every 6s for fresh count and pass
  // that into the prop instead. Bypasses the cache; doesn't disturb the rest of the dashboard.
  const [liveVisitorCount, setLiveVisitorCount] = useState(null);
  useEffect(() => {
    if (!shouldShowWelcome || !siteId || !token) {
      setLiveVisitorCount(null);
      return;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(
          `${API}/api/business/sessions?site_id=${siteId}&days=${daysRange}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && typeof json?.sessions === 'number') {
          setLiveVisitorCount(json.sessions);
        }
      } catch {
        /* swallow — next tick will retry */
      }
    };

    poll(); // fire immediately so the counter syncs on mount
    const interval = setInterval(poll, 6000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [shouldShowWelcome, siteId, token, daysRange, API]);

  // Show loading while fetching sites initially
  if (!token) {
    return (
      <LayoutWrapper>
        <div className="dashboard-page">
          <div className="dashboard-auth-required">
            <div className="dashboard-auth-icon">
              <Icon source={LockIcon} color="subdued" />
            </div>
            <h2>Authentication Required</h2>
            <p>Please log in to view your dashboard and analytics.</p>
            <Link to="/login" className="dashboard-auth-btn">
              Sign In to Continue
            </Link>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  // Show loading spinner while sites are being fetched
  if (sitesLoading || !sitesProcessed) {
    return (
      <LayoutWrapper>
        <div className="dashboard-page">
          <div className="dashboard-loading">
            <Spinner accessibilityLabel="Loading dashboard data" size="large" />
            <h2>Loading Dashboard</h2>
            <p>Fetching your sites and analytics...</p>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  const anyLoading =
    shouldFetchData &&
    (summaryLoading ||
      sessionsLoading ||
      overviewLoading ||
      topProductsLoading ||
      ga4Loading ||
      pageViewsLoading);
  const anyError =
    error ||
    savedPropertyError ||
    summaryError ||
    sessionsError ||
    overviewError ||
    topProductsError ||
    ga4Error ||
    pageViewsError;

  if (anyError) {
    return (
      <LayoutWrapper>
        <div className="dashboard-page">
          <div className="dashboard-error">
            <div className="dashboard-error-icon">
              <Icon source={AlertTriangleIcon} color="critical" />
            </div>
            <h2>Error Loading Dashboard</h2>
            <p className="dashboard-error-message">{anyError.message || anyError}</p>
            {!siteId && (
              <button className="dashboard-retry-btn" onClick={() => navigate('/how-it-works')}>
                Set up your first site
              </button>
            )}
            {siteId && (
              <button
                className="dashboard-retry-btn"
                onClick={() => {
                  const env = (platform || '').toLowerCase();
                  if (env === 'bigcommerce') navigate('/integrations/bigcommerce');
                  else if (env === 'woocommerce') navigate('/connect-woocommerce');
                  else if (env === 'shopify') navigate('/integrations');              // your Shopify page
                  else if (env === 'custom') navigate('/integrations/custom');        // or wherever your custom flow lives
                  else navigate('/sites'); // unknown/none => Add Site
                }}
              >
                Go to Integrations
              </button>
            )}
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  const kpiDetails = [
    {
      key: 'visitors',
      icon: TeamIcon,
      title: `Visitors (last ${daysRange}D)`,
      value: sessionsLoading ? <Spinner size="small" /> : sessionsData?.sessions ?? '—',
      sub: null,
      popup: {
        title: 'Visitors',
        body: `
**Definition:** Unique sessions in the last ${daysRange} day${daysRange > 1 ? 's' : ''}.

**Benchmark:** ${sessionsBaselineLoading || baselineDailyVisitors == null
            ? '—'
            : `${Math.round(expectedVisitorsForRange).toLocaleString()} — You should see about ${fmtDecimal(
              baselineDailyVisitors,
              1,
            )} new visitors per day (≈${Math.round(expectedVisitorsForRange).toLocaleString()} over ${daysRange} days).`
          }

**Why it matters:** Traffic is the top of your funnel—everything else depends on it.

**What to do:** Grow qualified traffic via SEO, paid/social, email, and partnerships.`,
      },
    },
    {
      key: 'orders',
      icon: OrderIcon,
      title: `Orders placed (last ${daysRange}D)`,
      value: fmtInt(orderCount),
      sub: null,
      popup: {
        title: 'Orders',
        body: `
**Definition:** Total orders placed in the last ${daysRange} day${daysRange > 1 ? 's' : ''}.

**Benchmark:** ${Number.isFinite(avgOrderCount)
            ? `${Math.round(expectedOrdersForRange).toLocaleString()} — You should see about ${fmtDecimal(
              avgOrderCount,
              1,
            )} orders per day (≈${Math.round(expectedOrdersForRange).toLocaleString()} over ${daysRange} days).`
            : '—'
          }

**Why it matters:** Direct signal of purchase activity and offer/channel effectiveness.

**What to do:** If down, check broken paths, stock/price/friction, and promos. If up, double-down on the channels/messages working now.`,
      },
    },
    {
      key: 'revenue',
      icon: WalletIcon,
      title: `Sales (last ${daysRange}D)`,
      value: fmtMoney(periodRevenue),
      sub: null,
      popup: {
        title: 'Revenue',
        body: `
**Definition:** Gross sales (incl. tax & shipping) over the last ${daysRange} day${daysRange > 1 ? 's' : ''}.

**Benchmark:** ${Number.isFinite(avgDailyRevenue)
            ? `${fmtMoney(Math.round(expectedRevenueForRange))} — You should see about ${fmtMoney(
              avgDailyRevenue,
            )} per day (≈${fmtMoney(Math.round(expectedRevenueForRange))} over ${daysRange} days).`
            : '—'
          }

**Why it matters:** A single number that reflects growth and run-rate health.

**What to do:** If soft, promote top SKUs, fix drop-offs, test urgency/bundles. If strong, scale the winning channels.`,
      },
    },
  ];

  const normPlatform = (platform || '').toLowerCase();
  const isBigCommerce = normPlatform === 'bigcommerce';
  const isWooCommerce = normPlatform === 'woocommerce';
  const isShopify = normPlatform === 'shopify';
  const isCustom = normPlatform === 'custom';
  //const hasKnownStore = isBigCommerce || isWooCommerce || isShopify || isCustom;

  const dashboardTitle = 'Your Analytics Dashboard';

  const dashboardSubtitle = isBigCommerce
    ? 'Store health and key numbers for your BigCommerce store'
    : isWooCommerce
      ? 'Store health and key numbers for your WooCommerce store'
      : isShopify
        ? 'Store health and key numbers for your Shopify store'
        : isCustom
          ? 'Website health and key numbers (custom integration)'
          : 'No store connected yet — add a site to see live store health';

  // First-time user with no data → full-takeover Welcome Hub
  if (shouldShowWelcome) {
    return (
      <LayoutWrapper>
        <div className="dashboard-page">
          <WelcomeHub
            storeName={storeName}
            storeUrl={storeUrl}
            firstName={null /* plug in user.firstName when available */}
            visitorCount={liveVisitorCount ?? sessionsData?.sessions ?? 0}
            onDismiss={dismissWelcome}
            onExploreNav={() => setShowNavCoachmark(true)}
          />
        </div>
        <NavCoachmark
          show={showNavCoachmark}
          onDismiss={() => setShowNavCoachmark(false)}
        />
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <div className="dashboard-page">
      <EmbeddedFullPageBanner /> 
        <div className="dashboard-header">
          <div className="dashboard-header-left">
            <div className="dashboard-header-badge">
              <span className="dashboard-status-dot"></span>
              System Active
            </div>
            <h1 className="dashboard-title">{dashboardTitle}</h1>
            <p className="dashboard-subtitle">{dashboardSubtitle}</p>
          </div>
        </div>

        <div className="dashboard-date-range-wrapper">
          <DateRangePills value={daysRange} onChange={handleRangeChange} />
        </div>

        <div className="dashboard-stats">
          {kpiDetails.map((kpi) => (
            <div key={kpi.key} className="dashboard-stat-card" onClick={() => handleOpenModal(kpi)}>
              <div className="dashboard-stat-icon">
                <Icon source={kpi.icon} color="interactive" />
              </div>
              <div className="dashboard-stat-content">
                <div className="dashboard-stat-value">{kpi.value}</div>
                {kpi.sub && <div className="dashboard-stat-sub">{kpi.sub}</div>}
                <div className="dashboard-stat-label">
                  <span className="dashboard-stat-label-text">{kpi.title}</span>
                  <span
                    className="info-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenModal(kpi);
                    }}
                  >
                    <Icon source={AlertCircleIcon} />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {anyLoading ? (
          <div className="dashboard-cards">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : (
          <div className="dashboard-cards story-cards">
{mainCardDetails.map((card) => (
  <StoryCard
    key={card.title}
    card={card}
    prescription={cardPrescriptions[card.title]}
    onCardClick={handleCardClick}
    handleOpenMainCardModal={handleOpenMainCardModal}
  />
))}
          </div>
        )}

        {activeSessions.length > 0 ? (
          <div className="dashboard-activity">
            <h3 className="dashboard-activity-title">Live visitors (now)</h3>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '-8px', marginBottom: '12px' }}>
              Active in the last 5 minutes
            </p>
            <div className="dashboard-activity-list">
              {activeSessions.slice(0, 5).map((session, index) => {
                const ts = pickStartTs(session);
                const minutesAgo = ts ? Math.floor((nowTs - ts) / 60000) : 0;

                const displayPath = session.entryPage || pickPath(session);
                const shortPath = displayPath.length > 30 ? displayPath.substring(0, 27) + '...' : displayPath;

                return (
                  <div key={session.sessionId || index} className="dashboard-activity-item">
                    <div
                      className="dashboard-activity-dot"
                      style={{ background: minutesAgo <= 1 ? 'var(--accent-2)' : 'var(--muted)' }}
                    ></div>
                    <div className="dashboard-activity-content">
                      <span className="dashboard-activity-text">Visitor browsing {shortPath}</span>
                      <span className="dashboard-activity-time">
                        {minutesAgo === 0 ? 'just now' : minutesAgo === 1 ? '1 min ago' : `${minutesAgo} min ago`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="dashboard-activity">
            <h3 className="dashboard-activity-title">Recent visitor activity</h3>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '-8px', marginBottom: '12px' }}>
              No one active right now
            </p>

            {recentSessions.length > 0 ? (
              <div className="dashboard-activity-list">
                {recentSessions.slice(0, 3).map((session, index) => {
                  const ts = pickStartTs(session);
                  const hoursAgo = ts ? Math.floor((nowTs - ts) / 3600000) : null;

                  const displayPath = pickPath(session);
                  const shortPath = displayPath.length > 30 ? displayPath.substring(0, 27) + '...' : displayPath;

                  return (
                    <div key={session.sessionId || index} className="dashboard-activity-item">
                      <div className="dashboard-activity-dot" style={{ background: 'var(--muted)' }}></div>
                      <div className="dashboard-activity-content">
                        <span className="dashboard-activity-text">Visitor browsed {shortPath}</span>
                        <span className="dashboard-activity-time">
                          {hoursAgo === null
                            ? ''
                            : hoursAgo === 0
                              ? 'less than an hour ago'
                              : hoursAgo === 1
                                ? '1 hour ago'
                                : hoursAgo < 24
                                  ? `${hoursAgo} hours ago`
                                  : 'more than a day ago'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p style={{ fontSize: '14px', color: 'var(--muted)', textAlign: 'center', padding: '20px' }}>
                No sessions recorded yet. We’ll light this up as soon as someone visits.
              </p>
            )}
          </div>
        )}

      </div>

      {modalActive && activeKpi && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{activeKpi.popup.title}</h2>
              <button className="modal-close-button" onClick={handleCloseModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <ReactMarkdown>{activeKpi.popup.body}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}

      {mainCardModalActive && activeMainCard && (
        <div className="modal-overlay" onClick={handleCloseMainCardModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{activeMainCard.popup.title}</h2>
              <button className="modal-close-button" onClick={handleCloseMainCardModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <ReactMarkdown>{activeMainCard.popup.body}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </LayoutWrapper>
  );
}